import { useEffect, useState } from 'react';
import { queue } from '../api/queueRuntime';
import type { QueueItem } from '../api/queue';
import { useT } from '../i18n/I18nProvider';
import { Icon } from './icons';

/**
 * Stav fronty a připojení.
 *
 * Skladník musí vidět rozdíl mezi „odesláno" a „odešle se, až bude signál".
 * Bez toho by si myslel, že hlášení dorazilo, i když leží v telefonu.
 */
export function useQueueItems(): QueueItem[] {
  const [items, setItems] = useState<QueueItem[]>([]);
  useEffect(() => queue.subscribe(setItems), []);
  return items;
}

export function useOnline(): boolean {
  const [online, setOnline] = useState(() => navigator.onLine);
  useEffect(() => {
    const up = () => setOnline(true);
    const down = () => setOnline(false);
    window.addEventListener('online', up);
    window.addEventListener('offline', down);
    return () => {
      window.removeEventListener('online', up);
      window.removeEventListener('offline', down);
    };
  }, []);
  return online;
}

export function QueueStatus() {
  const { t } = useT();
  const items = useQueueItems();
  const online = useOnline();

  const pending = items.filter((i) => i.state === 'pending').length;
  const failed = items.filter((i) => i.state === 'failed').length;

  if (online && pending === 0 && failed === 0) return null;

  if (failed > 0) {
    return (
      <button className="qbar err" type="button" onClick={() => void queue.retryFailed()}>
        <Icon name="warn" />
        <span>
          {t('queueFailed')} ({failed}) · {t('retryAction')}
        </span>
      </button>
    );
  }

  return (
    <div className="qbar">
      <Icon name="scan" />
      <span>
        {online ? t('queued') : t('offline')}
        {pending > 0 && ` (${pending})`}
      </span>
    </div>
  );
}
